import _, { cloneDeep } from 'lodash';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import EnhancedInput from '@components/_old/form/EnhancedInput';
import EnhancedInternationalPhoneInput from '@components/_old/form/EnhancedInternationalPhoneInput';
import EnhancedUploader from '@components/_old/form/EnhancedUploader';
import { validateDynamicForm } from '@utils/dynamic-utils';
import { isEmpty } from '@utils/validationUtils';
import {
  DynamicFormInputType,
  FormInputTypes,
  IDynamicSection,
  IDynamicWithSectionsForm,
} from './index';
import EnhancedCurrencyInput from '@components/_old/form/EnhancedCurrencyInput';
import EnhancedPercentageInput from '@components/_old/form/EnhancedPercentageInput';
import EnhancedInlinePaginatedChipInput from '@components/_old/form/EnhancedInlinePaginatedChipInput';
import NewChipsInput from '@components/_old/form/NewChipsInput';
import AutocompleteFormField from '@components/form-fields/AutocompleteFormField';
import EnhancedButton from '@components/button/EnhancedButton';
import { IAbstractRecord, IPagination } from '@models';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import SwitchFormField from '@components/form-fields/SwitchFormField';
import { MAIN_THEME } from '@styles';
import { CDN_URL } from '@utils';
import CheckboxFormField from '@components/form-fields/CheckboxFormField';
import EnhancedNumberInput from '../form/EnhancedNumberInput';

const useStyles = makeStyles<{ secondaryButton: boolean }>()(
  (theme, { secondaryButton }) => ({
    form: {
      width: '100%',
    },
    submitBtn: {
      margin: '0 auto 25px auto!important',
      display: 'flex!important',
    },
    footerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: secondaryButton ? 'space-between' : 'center',
    },
    titleStyling: {
      fontFamily: MAIN_THEME.font.bold,
      lineHeight: '18px',
    },
  })
);

const SectionDynamicForm: React.FC<
  IDynamicWithSectionsForm & {
    children?: React.ReactNode;
    onCustomBlur?: (
      fieldName: string,
      values: Record<string, any>,
      errors: Record<string, any>,
      subFieldName?: string,
      index?: number
    ) => any;
    onCustomValidate?: (
      values: Record<string, any>,
      errors: Record<string, any>
    ) => Promise<Record<string, string>>;
    onSearch?: (fieldName: string, value: string) => Promise<void>;
    onAutocompleteSearch?: (
      fieldName: string,
      value: string,
      pagination: IPagination
    ) => Promise<{ options: Record<string, string>; totalCount: number }>;
  }
> = ({
  title,
  sections,
  buttonText,
  onSubmit,
  disableForm = false,
  submitButtonState = null,
  isSubmitButtonDisabled = false,
  children,
  secondaryButton = false,
  secondaryButtonText = '',
  onClickSecondaryButton = () => undefined,
  loadingFields = {},
  customStyles,
  onChange,
  hasDoprdownSpecificBehavior = false,
  onCustomBlur,
  onCustomValidate,
  onSearch,
  onAutocompleteSearch,
}) => {
  const [values, setValues] = useState<Record<string, any>>();
  const [errors, setErrors] = useState<Record<string, any>>();
  const [touched, setTouched] = useState<Record<string, any>>();

  const [validatingForm, setValidatingForm] = useState<boolean>(false);

  const { classes } = useStyles({ secondaryButton });

  const inputs = useMemo(() => {
    let inputs: Record<string, DynamicFormInputType> = {};

    Object.values(sections).forEach((section) => {
      inputs = { ...inputs, ...section.inputs };
    });
    return inputs;
  }, [sections]);

  useEffect(() => {
    const initialValues: Record<string, any> = values || {};
    const initialErrors: Record<string, string> = errors || {};
    const initialTouched: Record<string, any> = touched || {};
    let sectionInputs: Record<string, DynamicFormInputType> = {};

    let mappedValues = _.cloneDeep(values);

    if (!mappedValues) {
      mappedValues = {};

      Object.keys(inputs).forEach((inputKey) => {
        mappedValues[inputKey] = inputs[inputKey]?.value;
      });

      Object.values(sections).forEach((section) => {
        sectionInputs = { ...sectionInputs, ...section.inputs };
      });

      Object.values(sectionInputs).forEach((input) => {
        initialValues[input.name] = mappedValues[input.name];
        initialErrors[input.name] =
          initialErrors[input.name] || input.error || '';
        initialTouched[input.name] =
          initialTouched[input.name] || !!input.error;
      });

      setValues(initialValues);
      setErrors(initialErrors);
      setTouched(initialTouched);
    }
  }, [sections]);

  const onFieldBlur = async (
    name: string,
    additionalProperties?: {
      subFieldName?: string;
      index?: number;
      input?: DynamicFormInputType;
    }
  ) => {
    const { subFieldName, index, input } = additionalProperties || {};

    const touchedTemp = Object.assign({}, touched);
    touchedTemp[name] = true;

    if (onCustomBlur) {
      setValidatingForm(true);
      const result = await onCustomBlur(
        name,
        cloneDeep(values),
        cloneDeep(errors),
        subFieldName,
        index
      );
      setValidatingForm(false);
      setValues(result.values);
      setErrors(result.errors);
    }

    setTouched(touchedTemp);
  };

  const onFieldChange = (
    name: string,
    value: any,
    additionalProperties?: {
      input?: DynamicFormInputType;
    }
  ) => {
    const { input } = additionalProperties || {};
    const errorsTemp = Object.assign({}, errors);
    const valuesTemp = Object.assign({}, values);
    const touchedTemp = Object.assign({}, touched);
    valuesTemp[name] = value;
    errorsTemp[name] = validateDynamicForm(value, inputs[name], valuesTemp);
    if (onChange) {
      onChange(
        name,
        value,
        valuesTemp,
        errorsTemp,
        touchedTemp,
        additionalProperties
      );
    }
    setValues(valuesTemp);
    setErrors(errorsTemp);
    setTouched(touchedTemp);
  };

  const onFieldChangeByEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange(event.target.name, event.target.value);
  };

  const validate = async (): Promise<IAbstractRecord> => {
    let newErrors: IAbstractRecord = {};
    Object.values(inputs).forEach((input) => {
      newErrors[input.name] = validateDynamicForm(
        values[input.name],
        input,
        values
      );
    });

    if (onCustomValidate) {
      newErrors = await onCustomValidate(values, newErrors);
    }

    return newErrors;
  };

  const submit = async () => {
    if (!disableForm && !validatingForm) {
      setValidatingForm(true);
      const validateErrors = await validate();
      setValidatingForm(false);
      const isInValid = Object.values(validateErrors).some((v) => {
        if (Array.isArray(v)) {
          return v.some((e) => {
            return Object.values(e).some((i) => !isEmpty(i));
          });
        }
        return !isEmpty(v);
      });
      if (isInValid) {
        const initialTouched: Record<string, boolean> = {};
        Object.values(inputs).forEach((input) => {
          initialTouched[input.name] = true;
        });
        setErrors(validateErrors);
        setTouched(initialTouched);
      } else {
        onSubmit(values);
      }
    }
  };

  const renderInput = (input: DynamicFormInputType) => {
    const disabledInput =
      input.disabled ||
      (input.conditionalDisable && input.conditionalDisable(values)) ||
      disableForm ||
      validatingForm;

    const hidden =
      input.hidden ||
      (input.conditionalHidden && input.conditionalHidden(values));

    if (hidden) {
      return <></>;
    }

    const isRequired =
      input.required ||
      (input.conditionalRequired && input.conditionalRequired(values));
    const title = isRequired ? `${input.title}*` : input.title;

    switch (input.type) {
      case FormInputTypes.text:
        return (
          <EnhancedInput
            key={input.name}
            name={input.name}
            title={title}
            type="text"
            placeholder={input.placeholder}
            value={values[input.name]}
            error={errors[input.name]}
            onBlur={(event) => {
              const newValue = event.target.value;
              onFieldBlur(input.name);
              if (!isEmpty(newValue)) {
                onFieldChange(input.name, newValue);
                if (input.onChange) {
                  input.onChange(event);
                }
              }
            }}
            onChange={(v) => onFieldChange(input.name, v.target.value)}
            disabled={disabledInput}
            description={input.description}
            multiline={input.multiline}
            material={input.material}
            customStyles={input.customStyles}
          />
        );
      case FormInputTypes.checkbox:
        return (
          <CheckboxFormField
            key={input.name}
            name={input.name}
            title={title}
            checked={values[input.name]}
            onChange={(name, val) => onFieldChange(input.name, val)}
            disabled={disabledInput || disableForm}
          />
        );
      case FormInputTypes.password:
        return (
          <EnhancedInput
            key={input.name}
            name={input.name}
            title={title}
            type="password"
            placeholder={input.placeholder}
            value={values[input.name]}
            error={errors[input.name]}
            onBlur={() => onFieldBlur(input.name)}
            onChange={(v) => onFieldChange(input.name, v.target.value)}
            disabled={disabledInput}
            material={input.material}
            description={input.description}
            includePasswordhint={input.includePasswordHint}
            includePasswordVisibility={input.includePasswordVisibility}
            includeCapsLockCheck={input.includeCapsLockCheck}
            customStyles={input.customStyles}
          />
        );

      case FormInputTypes.imageFile:
        return (
          <EnhancedUploader
            key={input.name}
            name={input.name}
            title={title}
            value={values[input.name]}
            error={errors[input.name]}
            onBlur={() => onFieldBlur(input.name)}
            onUpload={(v) => onFieldChange(input.name, v)}
            disabled={disabledInput}
            allowedFileTypes={input.allowedFileTypes}
            type={''}
          />
        );

      case FormInputTypes.select:
        return <span>deprecated</span>;
      case FormInputTypes.number:
        return (
          <EnhancedNumberInput
            key={input.name}
            name={input.name}
            title={title}
            placeholder={input.placeholder}
            value={values[input.name]}
            error={errors[input.name]}
            onBlur={() => onFieldBlur(input.name)}
            onChange={(v) => onFieldChangeByEvent(v)}
            disabled={disabledInput}
            material={input.material}
            minValue={input.minNumber}
            maxValue={input.maxNumber}
          />
        );
      case FormInputTypes.percentage:
        return (
          <EnhancedPercentageInput
            key={input.name}
            name={input.name}
            title={title}
            placeholder={input.placeholder}
            value={values[input.name]}
            error={errors[input.name]}
            onBlur={() => onFieldBlur(input.name)}
            onChange={(v) => onFieldChangeByEvent(v)}
            disabled={disabledInput}
            material={input.material}
          />
        );

      case FormInputTypes.switch:
        return (
          <SwitchFormField
            key={input.name}
            name={input.name}
            title={title}
            value={values[input.name]}
            onBlur={() => onFieldBlur(input.name)}
            onChange={(name, val) => onFieldChange(input.name, val)}
            disabled={disabledInput}
            required={isRequired}
          />
        );

      case FormInputTypes.multiSelect:
        return <span>deprecated</span>;
      case FormInputTypes.chips:
        return hasDoprdownSpecificBehavior && input.hasPagination ? (
          <EnhancedInlinePaginatedChipInput
            key={input.name}
            name={input.name}
            title={title}
            placeholder={input.placeholder}
            value={values[input.name]}
            preselectedValues={input.preselectedValues}
            error={touched[input.name] ? errors[input.name] : ''}
            onChange={(v) => {
              onFieldChange(input.name, v);
              if (!isEmpty(v) && input.onSelect) {
                input.onSelect(v as string);
              }
              const newValue = v;
              onFieldBlur(input.name);
              if (!isEmpty(newValue)) {
                onFieldChange(input.name, newValue);
                if (input.onChange) {
                  input.onChange(newValue);
                }
              }
            }}
            disabled={disabledInput}
            customStyles={input.customStyles}
            selectOptions={input.selectOptions}
            required={isRequired}
            freeSolo={input.freeSolo}
            multiple={input.multiple}
            material={input.material}
            loader={loadingFields[input.name] || false}
            showSelectAll={input.showSelectAll}
            strongStyledOption={input.strongStyledOption}
            hidden={hidden}
            itemsPerPage={input.itemsPerPage}
          />
        ) : (
          <NewChipsInput
            key={input.name}
            name={input.name}
            title={title}
            multiple={input.multiple}
            required={input.required}
            value={values[input.name]}
            values={values[input.name] || []}
            disabled={disabledInput}
            items={input.selectOptions}
            material={input.material}
            onSearch={
              onSearch
                ? async (v) => {
                    if (onSearch) {
                      await onSearch(input.name, v);
                    }
                  }
                : null
            }
            placeholder={input.placeholder}
            showSelectAll={input.showSelectAll}
            error={touched[input.name] ? errors[input.name] : ''}
            onChange={(v) => {
              onFieldChange(input.name, v);
              if (!input.multiple) {
                if (!isEmpty(v) && input.onSelect) {
                  input.onSelect(v as string);
                }
              }
            }}
          />
        );
      case FormInputTypes.phoneNumber:
        return (
          <EnhancedInternationalPhoneInput
            key={input.name}
            name={input.name}
            title={title}
            placeholder={input.placeholder}
            value={values[input.name]}
            error={errors[input.name]}
            onChange={(v) => onFieldChange(input.name, v)}
            onBlur={() => onFieldBlur(input.name)}
            disabled={disabledInput}
            countriesToShow={input.countriesToShow}
            disableDropDown={input.disableDropDown}
            editCountryCode={input.editCountryCode}
            defaultCountry={input.defaultCountry}
            customFormat={input.customFormat}
            material={input.material}
          />
        );
      case FormInputTypes.currency:
        return (
          <EnhancedCurrencyInput
            key={input.name}
            name={input.name}
            title={title}
            placeholder={input.placeholder}
            value={values[input.name]}
            error={touched[input.name] ? errors[input.name] : ''}
            onBlur={() => onFieldBlur(input.name)}
            onChange={(v) => onFieldChangeByEvent(v)}
            disabled={disabledInput}
            type={''}
            useCurrencyText={input.useCurrencySymbol}
            currencyText={input.useCurrencySymbol ? input.currencySymbol : ''}
            currencyTitle={input.useCurrencySymbol ? null : '$'}
            currencyIcon={
              input.useCurrencySymbol
                ? null
                : CDN_URL + '/icons/dollar-primary.svg'
            }
            onFocus={() => onFieldBlur(input.name)}
          />
        );
      case FormInputTypes.newautocomplete:
        return (
          <AutocompleteFormField
            key={input.name}
            name={input.name}
            title={title}
            placeholder={input.placeholder}
            value={values[input.name]}
            error={touched[input.name] ? errors[input.name] : ''}
            onChange={(v) => {
              onFieldChange(input.name, v);
            }}
            disabled={disabledInput}
            material={input.material}
            loader={loadingFields[input.name] || false}
            maxItemsDisplayed={input.maxItemsDisplayed}
            fetchOptions={function (
              query: string,
              pagination: IPagination
            ): Promise<{
              options: Record<string, string>;
              totalCount: number;
            }> {
              if (onAutocompleteSearch) {
                return onAutocompleteSearch(input.name, query, pagination);
              }

              return new Promise<{
                options: Record<string, string>;
                totalCount: number;
              }>((resolve) => {
                resolve({ options: {}, totalCount: 0 });
              });
            }}
          />
        );
      case FormInputTypes.date:
      case FormInputTypes.newdate:
        return (
          <DatePickerFormField
            key={input.name}
            name={input.name}
            title={title}
            value={values[input.name]}
            error={errors[input.name]}
            onBlur={() => onFieldBlur(input.name)}
            onDateChange={(dateValue) => {
              onFieldChange(input.name, dateValue);

              input.value = dateValue;

              if (input.onChange) {
                input.onChange(dateValue);
              }
            }}
            disabled={disabledInput}
            material={input.material}
            minDate={input.minDate}
            maxDate={input.maxDate}
            placeholder={input.placeholder}
            canClearDate={input.canClearDate}
          />
        );
      case FormInputTypes.newselect:
        return input.injectGlobalLov ? (
          <SelectFormField
            key={input.name}
            name={input.name}
            title={title}
            placeholder={input.placeholder}
            value={values[input.name]}
            disabled={disabledInput}
            injectGlobalLov={input.injectGlobalLov}
            loader={loadingFields[input.name] || false}
            freeSolo={input.freeSolo}
            material={input.material}
            error={touched[input.name] ? errors[input.name] : ''}
            onChange={(value) => {
              onFieldChange(input.name, value);
            }}
            onBlur={() => {
              onFieldBlur(input.name);
            }}
            canClearValue={input.canClearValue}
          />
        ) : (
          <SelectFormField
            key={input.name}
            name={input.name}
            title={title}
            placeholder={input.placeholder}
            value={values[input.name]}
            disabled={disabledInput}
            selectOptions={input.selectOptions}
            loader={loadingFields[input.name] || false}
            freeSolo={input.freeSolo}
            material={input.material}
            error={touched[input.name] ? errors[input.name] : ''}
            onChange={(value) => {
              onFieldChange(input.name, value);
            }}
            onBlur={() => {
              onFieldBlur(input.name);
            }}
            canClearValue={input.canClearValue}
          />
        );

      default:
        return <></>;
    }
  };

  const renderSection = (section: IDynamicSection) => {
    return (
      <Fragment key={section.title}>
        <div className={classes.titleStyling}>
          <p>
            {section.hasTitleSpecificDesign
              ? section.specificTitleDesign()
              : section.title}
          </p>
          <hr style={{ opacity: '0.25' }} />
          <br />
        </div>
        {Object.values(section.inputs).map((input) => renderInput(input))}
      </Fragment>
    );
  };

  return values ? (
    <form
      className={classes.form}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <p>{title}</p>
      {Object.values(sections).map((section) => renderSection(section))}

      {children}
      <div className={classes.footerContainer}>
        {secondaryButton && (
          <EnhancedButton
            onClick={() => onClickSecondaryButton()}
            disabled={disableForm}
            variant="contained"
            className={classes.submitBtn}
            state={submitButtonState}
          >
            {secondaryButtonText}
          </EnhancedButton>
        )}

        <EnhancedButton
          type="submit"
          disabled={
            disableForm ||
            isSubmitButtonDisabled ||
            Object.values(errors).some((v) => {
              if (Array.isArray(v)) {
                return v.some((e) => {
                  return Object.values(e).some((i) => !isEmpty(i));
                });
              }
              return !isEmpty(v);
            })
          }
          isPrimary
          state={submitButtonState}
          className={customStyles && customStyles.submitButtonStyles}
        >
          {buttonText}
        </EnhancedButton>
      </div>
    </form>
  ) : (
    <></>
  );
};

export default SectionDynamicForm;
